import React from 'react'
import SeoPage from '../components/SeoPage'

import Img from '../components/Image'
import { graphql, Link } from 'gatsby'

const ContentPage = ({ data, location, ...props }) => {
    return (
        <SeoPage
            leadImage={data.img0.childImageSharp.fluid.src}
            location={location}
            title="Combien ça coûte une accusation de conduite avec facultés affaiblies?"
            description="Découvrez quels sont les coûts liés à une arrestation et une accusation pour conduite avec les facultés affaiblies au Québec!"
            fr={undefined}
            lawType="criminal"
            specialtyText="pour Alcool au volant">
            <p>
                <strong>
                    Au Québec, une accusation de conduite avec les facultés
                    affaiblies peut entraîner des coûts importants, même sans
                    condamnation!
                </strong>
            </p>
            <p>
                Dès l’arrestation, des dépenses s’accumulent rapidement, et
                plusieurs démarches deviennent nécessaires pour se défendre.{' '}
            </p>
            <p>
                Bien souvent, les conducteurs ne réalisent pas à quel point les
                conséquences financières d’une accusation peuvent être lourdes.{' '}
            </p>
            <p>
                <Img
                    fluid={data.img0.childImageSharp.fluid}
                    title="Coûts d’une arrestation pour conduite avec facultés affaiblies!"
                    alt=""
                />
            </p>
            <table>
                <tr>
                    <td>
                        <p>
                            <strong>
                                Dans cet article, nous vous présentons un aperçu
                                des coûts associés à une accusation de conduite
                                avec les facultés affaiblies!
                            </strong>
                        </p>
                    </td>
                </tr>
            </table>
            <h2>
                Quels sont les coûts engendrés par une accusation de conduite
                avec les facultés affaiblies?
            </h2>
            <p>
                Au Québec, une accusation pour conduite avec facultés affaiblies
                entraîne de nombreuses conséquences, souvent accompagnées de
                frais importants. Dès l’arrestation, le conducteur peut faire
                face à des sanctions administratives et judiciaires qui génèrent
                diverses dépenses. Même sans condamnation, les démarches exigées
                pour se défendre peuvent devenir coûteuses.{' '}
            </p>
            <p>
                Voici un aperçu des coûts possibles lorsqu’une personne est
                arrêtée et accusée de conduite avec facultés affaiblies :
            </p>
            <h3>SUSPENSION IMMÉDIATE DU PERMIS DE CONDUIRE</h3>
            <p>
                Peu importe l’issue du procès, une personne arrêtée pour
                conduite avec les facultés affaiblies verra son permis suspendu
                automatiquement dès l’arrestation. Cette suspension
                administrative s’applique même si le conducteur n’a pas encore
                été reconnu coupable. Selon les circonstances, la durée de la
                suspension peut être de 24 heures ou de 90 jours.
            </p>
            <ul>
                <li>
                    La <strong>suspension de 24 heures </strong>s’applique si la
                    personne échoue aux tests de coordination.{' '}
                </li>
                <li>
                    La <strong>suspension de 90 jours</strong> s’applique
                    lorsque le taux d’alcool dans le sang du conducteur est de
                    80 mg par 100 ml ou plus, ou encore si le conducteur a
                    refusé d’obéir à un policier (par exemple, en{' '}
                    <Link to="https://avocatsalcoolauvolant.ca/refus-souffler-alcootest/">
                        <strong>refusant de souffler dans l’éthylomètre</strong>
                    </Link>{' '}
                    ou de se soumettre aux tests de coordination).
                </li>
            </ul>
            <p>
                Il est important de noter que, durant cette suspension
                administrative, il n’est pas possible d’obtenir un permis
                restreint!
            </p>
            <h4>
                Coût indirect immédiat lié au fait de devoir se déplacer sans
                permis de conduire
            </h4>
            <p>
                Cette suspension administrative du permis de conduire peut
                représenter un défi logistique important pour plusieurs. Sans la
                possibilité de conduire, il devient nécessaire de trouver des
                moyens de transport alternatifs, ce qui engendre des coûts
                souvent non négligeables.{' '}
            </p>
            <p>
                Que ce soit en ayant recours aux transports en commun, en
                prenant un taxi ou encore en demandant de l’aide à des proches,
                des frais peuvent s’accumuler rapidement. Pour une personne
                vivant en région ou ayant des obligations professionnelles qui
                exigent la mobilité, ces coûts peuvent devenir particulièrement
                lourds.
            </p>
            <h4>Il est possible de contester la suspension administrative </h4>
            <p>
                Un conducteur dont le permis a été suspendu administrativement
                peut contester cette suspension en faisant une demande de
                révision de la décision à la SAAQ. Toutefois, cette demande de
                révision s’appuie sur des motifs très limités, notamment :
            </p>
            <ul>
                <li>
                    Si le conducteur était soumis à la règle du zéro alcool et
                    ne présentait aucune trace d’alcool dans son organisme.
                </li>
                <li>
                    Si le taux d’alcool du conducteur était inférieur à 80 mg
                    par 100 ml de sang.
                </li>
                <li>
                    Si le conducteur disposait d’une excuse raisonnable pour
                    refuser de se soumettre à un test demandé par un agent de la
                    paix.
                </li>
                <li>
                    Si le conducteur ne conduisait pas ou{' '}
                    <Link to="https://avocatsalcoolauvolant.ca/accusation-controle-vehicule-alcool-volant/">
                        <strong>n’avait pas la garde du véhicule</strong>
                    </Link>{' '}
                    au moment des faits.
                </li>
            </ul>
            <p>
                Pour contester la suspension, le conducteur doit d’abord
                préparer une demande de révision en remplissant un formulaire
                officiel, accompagné du procès-verbal de suspension.{' '}
            </p>
            <p>
                <Img
                    fluid={data.img1.childImageSharp.fluid}
                    title="Contester une suspension du permis de conduire!"
                    alt=""
                />
            </p>
            <p>
                Selon le cas, il devra également joindre des documents comme un
                certificat d’analyse d’échantillon d’haleine ou de sang, un
                rapport d’évaluateur en cas d’affaiblissement par drogue, ainsi
                que tout document utile à sa défense, par exemple un rapport
                médical.
            </p>
            <p>
                La demande peut être déposée en personne ou par la poste. Une
                rencontre est organisée dans les 10 jours suivant la demande,
                permettant au conducteur de présenter des témoins ou des
                experts. La décision de la SAAQ est rendue 10 jours après la
                rencontre. En cas de refus, le conducteur peut contester la
                décision devant le Tribunal administratif du Québec dans un
                délai de 10 jours.
            </p>
            <h3>
                <strong>
                    <strong>SAISIE DU VÉHICULE</strong>
                </strong>
            </h3>
            <p>
                Au Québec, lorsqu’un conducteur est arrêté pour conduite avec
                les facultés affaiblies, son véhicule peut être saisi et
                remorqué pendant <strong>30 jours</strong>, et ce, à ses frais.
                Cette mesure s’applique dans les cas :
            </p>
            <ul>
                <li>
                    Si l’alcoolémie dépasse 160 mg d’alcool par 100 ml de sang.
                </li>
                <li>
                    Si le conducteur refuse de se soumettre à un alcootest ou à
                    d’autres tests exigés par les policiers.
                </li>
            </ul>
            <p>
                Le coût du remorquage s’élève généralement à environ 125 $. À
                cela s’ajoutent les frais de remisage, qui peuvent atteindre 25
                $ par jour. Pour une période de saisie de 30 jours, la facture
                totale grimpe donc à environ 875 $, sans compter d’éventuels
                frais additionnels en fonction du lieu et des circonstances.
            </p>
            <table>
                <tr>
                    <td>
                        <p>
                            <strong>
                                Saisie du véhicule de 90 jours en cas de
                                récidive
                            </strong>
                        </p>
                        <p>
                            Il est important de noter que lorsqu’il s’agit d’une{' '}
                            <Link to="https://avocatsalcoolauvolant.ca/recidive-alcool-volant/">
                                <strong>récidive</strong>
                            </Link>{' '}
                            dans les 10 dernières années, c’est-à-dire que le
                            conducteur a déjà été reconnu coupable de conduite
                            avec les facultés affaiblies durant cette période,
                            la saisie du véhicule est obligatoire pour une durée
                            de 90 jours.{' '}
                        </p>
                        <p>
                            Dans ce cas, dès que le taux d’alcool est égal ou
                            supérieur à 80 mg, la saisie du véhicule est
                            automatique pour 90 jours. Il n’est pas nécessaire
                            que le taux dépasse 160 mg, car le simple fait qu’il
                            s’agisse d’une récidive suffit pour justifier la
                            saisie.
                        </p>
                    </td>
                </tr>
            </table>
            <h4>Demande de mainlevée de saisie</h4>
            <p>
                Dans certains cas, lorsque le conducteur arrêté n’est pas le
                propriétaire du véhicule saisi, il est possible de demander la
                mainlevée de la saisie. Cette démarche permet au véritable
                propriétaire de récupérer son véhicule avant la fin de la
                période prévue.{' '}
            </p>
            <p>
                <Img
                    fluid={data.img2.childImageSharp.fluid}
                    title="Saisie du véhicule pour conduite avec facultés affaiblies!"
                    alt=""
                />
            </p>
            <p>Pour récupérer le véhicule, le propriétaire peut démontrer :</p>
            <ul>
                <li>
                    Qu’il n’avait pas donné son consentement pour que le
                    conducteur prenne possession de son véhicule.
                </li>
                <li>
                    Qu’il ne pouvait pas prévoir que le conducteur conduirait
                    avec un taux d’alcool égal ou supérieur à 80 mg par 100 ml
                    de sang, ou refuserait de se soumettre à un alcootest ou à
                    d’autres tests exigés par les policiers.
                </li>
            </ul>
            <p>
                <strong>
                    La demande doit être déposée auprès de la SAAQ à l’aide du
                    formulaire prévu à cet effet.{' '}
                </strong>
            </p>
            <p>
                Si une décision favorable autorisant la mainlevée est rendue,
                cela permet au propriétaire de récupérer rapidement son
                véhicule. Il est toutefois important de noter que les frais de
                remorquage et de remisage demeurent à la charge du propriétaire
                et non du conducteur.
            </p>
            <h3>FRAIS D’AVOCAT</h3>
            <p>
                Lorsqu’une personne est accusée de conduite avec les facultés
                affaiblies, il est fortement recommandé de consulter un avocat
                spécialisé en droit criminel. Dès l’arrestation, l’avocat veille
                au respect des droits de l’accusé et le représente devant les
                instances judiciaires.{' '}
            </p>
            <ul>
                <li>
                    <strong>
                        L’intervention d’un avocat peut faire la différence
                        entre une déclaration de culpabilité et un{' '}
                    </strong>
                    <Link to="https://avocatsalcoolauvolant.ca/exemples-acquittement-alcool-volant/">
                        <strong>acquittement</strong>
                    </Link>
                    <strong>!</strong>
                </li>
            </ul>
            <p>
                En effet, un avocat spécialisé dans le domaine est en mesure de
                formuler des arguments solides et de mettre en œuvre des moyens
                de défense efficaces pour soulever un doute raisonnable, ce qui
                peut mener à l’acquittement du conducteur accusé de conduite
                avec les facultés affaiblies.
            </p>
            <h4>Combien coûte un avocat?</h4>
            <p>
                Bien que le recours à un avocat soit fortement recommandé, il
                s’agit d’un service qui engendre des coûts non négligeables. Le
                montant total dépend de plusieurs facteurs, notamment la
                complexité du dossier, l’expérience du professionnel engagé et
                la région où se déroule la procédure.
            </p>
            <ul>
                <li>
                    <strong>En moyenne,</strong>{' '}
                    <strong>
                        les honoraires pour une défense dans une affaire de
                        conduite avec facultés affaiblies varient entre 1 500 $
                        et 10 000 $.
                    </strong>{' '}
                </li>
            </ul>
            <p>
                Les avocats facturent généralement leurs honoraires selon deux
                méthodes principales :
            </p>
            <ol>
                <li>
                    La première est la <strong>tarification à l’heure</strong>,
                    où le client paie en fonction du nombre d’heures consacrées
                    au dossier, avec un tarif horaire variant généralement entre
                    200 $ et 500 $, selon l’expérience de l’avocat et la région.
                    Ce mode peut devenir coûteux si l’affaire dure longtemps ou
                    nécessite plusieurs comparutions.{' '}
                </li>
                <li>
                    La deuxième méthode est le{' '}
                    <strong>tarif forfaitaire</strong>, qui consiste en un
                    montant fixe couvrant l’ensemble du processus, de
                    l’arrestation jusqu’au verdict, souvent proposé pour les
                    dossiers de conduite avec facultés affaiblies moins
                    complexes.
                </li>
            </ol>
            <h2>Qu’est-ce qui influence le coût d’un avocat?</h2>
            <p>
                Le montant des frais d’un avocat dans une affaire de conduite
                avec facultés affaiblies varie principalement selon les facteurs
                suivants :
            </p>
            <table>
                <tr>
                    <td>
                        <p>
                            <strong>Facteurs</strong>
                        </p>
                    </td>
                    <td>
                        <p>
                            <strong>Explication</strong>
                        </p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>
                            <strong>La nature du dossier</strong>
                        </p>
                    </td>
                    <td>
                        <p>
                            Dans les dossiers simples, comme une première
                            infraction avec un taux d’alcoolémie légèrement
                            au-dessus de la limite légale, les frais se situent
                            généralement dans la tranche inférieure.{' '}
                        </p>
                        <p>
                            En revanche, si l’affaire présente des éléments
                            aggravants, par exemple un taux d’alcool deux fois
                            supérieur à la limite permise (160 mg/100 ml), des
                            dommages corporels, un accident ou encore des
                            antécédents judiciaires, les coûts peuvent augmenter
                            de façon importante.
                        </p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>
                            <strong>L’expérience de l’avocat</strong>
                        </p>
                    </td>
                    <td>
                        <p>
                            Plus un avocat est expérimenté dans les dossiers de
                            conduite avec les facultés affaiblies et reconnu en
                            droit criminel, plus ses honoraires sont élevés.{' '}
                        </p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>
                            <strong>La situation géographique</strong>
                        </p>
                    </td>
                    <td>
                        <p>
                            Les tarifs des avocats sont souvent plus élevés à
                            Montréal, Laval, Québec et dans les grandes villes
                            qu’en région ou dans les petites municipalités. Cela
                            s’explique par un coût de la vie plus important et
                            une demande plus forte dans les grands centres
                            urbains.
                        </p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>
                            <strong>Les frais additionnels</strong>
                        </p>
                    </td>
                    <td>
                        <p>
                            En plus des honoraires de l’avocat, des frais
                            supplémentaires peuvent s’ajouter, tels que les
                            frais judiciaires, les honoraires d’experts
                            spécialisés (toxicologues, reconstituteurs
                            d’accidents, etc.) ou les frais de communication
                            nécessaires au traitement du dossier.{' '}
                        </p>
                        <p>
                            Il en va de même pour les frais liés aux
                            déplacements, que ce soit pour se rendre au poste de
                            police ou au palais de justice.
                        </p>
                    </td>
                </tr>
            </table>
            <p>
                <Img
                    fluid={data.img3.childImageSharp.fluid}
                    title="Coûts d’un avocat pour conduite avec les facultés affaiblies!"
                    alt=""
                />
            </p>
            <p>
                Même si les honoraires d’un avocat peuvent représenter une
                dépense importante, ils doivent être considérés comme un
                investissement essentiel pour protéger ses droits et maximiser
                ses chances d’être acquitté dans une affaire de conduite avec
                les facultés affaiblies.
            </p>
            <h2>
                Peut-on bénéficier de l’aide juridique en cas d’accusation de
                conduite avec les facultés affaiblies?
            </h2>
            <p>
                <strong>Oui!</strong> Une personne accusée de conduite avec les
                facultés affaiblies peut avoir droit à l’aide juridique afin de
                couvrir tout ou partie des frais liés à sa défense. Ce service,
                offert par le gouvernement du Québec, vise à garantir un accès
                équitable à la justice pour les personnes dont les ressources
                financières sont limitées.
            </p>
            <p>
                Pour bénéficier de l’aide juridique, la personne concernée doit
                démontrer que sa situation financière lui donne droit à cette
                aide, conformément aux barèmes d’admissibilité établis par la
                loi.
            </p>
            <p>
                Ces barèmes permettent de déterminer si la personne peut
                recevoir l’aide juridique gratuitement, si elle doit verser une
                contribution fixe variant entre 100 et 800 $, ou si elle n’est
                pas admissible du tout, selon son niveau de revenu et sa
                situation familiale.
            </p>
            <h4>
                <strong>
                    <strong>
                        Voici les barèmes des revenus bruts annuels (juillet
                        2025) pour bénéficier de l’aide juridique gratuite :
                    </strong>
                </strong>
            </h4>
            <table>
                <tr>
                    <td>
                        <p>
                            <strong>Situation familiale</strong>
                        </p>
                    </td>
                    <td>
                        <p>
                            <strong>Barème des revenus annuels (bruts)</strong>
                        </p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Personne seule</p>
                    </td>
                    <td>
                        <p>29 302 $</p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Famille formée d’un adulte et d’un enfant</p>
                    </td>
                    <td>
                        <p>35 854 $</p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>
                            Famille formée d’un adulte et de deux enfants ou
                            plus
                        </p>
                    </td>
                    <td>
                        <p>38 275 $</p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Famille formée de conjoints sans enfant</p>
                    </td>
                    <td>
                        <p>40 782 $</p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Famille formée de conjoints avec un enfant</p>
                    </td>
                    <td>
                        <p>45 630 $</p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>
                            Famille formée de conjoints avec deux enfants ou
                            plus
                        </p>
                    </td>
                    <td>
                        <p>48 053 $</p>
                    </td>
                </tr>
            </table>
            <p>
                Si une personne accusée de conduite avec les facultés affaiblies
                n’est pas admissible à l’aide juridique gratuite, elle peut tout
                de même y avoir accès en versant une contribution fixe, selon sa
                situation financière.{' '}
            </p>
            <p>
                L’admissibilité à cette aide moyennant une contribution, qui
                varie entre 100 $ et 800 $, est évaluée en fonction du revenu
                réputé, lequel tient compte des revenus réels et de la situation
                familiale.
            </p>
            <h4>
                Voici les barèmes des revenus bruts annuels (juillet 2025) pour
                bénéficier de l’aide juridique avec contribution :
            </h4>
            <table>
                <tr>
                    <td>
                        <p>
                            <strong>Situation familiale</strong>
                        </p>
                    </td>
                    <td>
                        <p>
                            <strong>Revenus réputés</strong>
                        </p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Personne seule</p>
                    </td>
                    <td>
                        <p>29 303 $ à 40 933 $</p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Famille formée d’un adulte et d’un enfant</p>
                    </td>
                    <td>
                        <p>35 855 $ à 50 069 $</p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>
                            Famille formée d’un adulte et de deux enfants ou
                            plus
                        </p>
                    </td>
                    <td>
                        <p>38 276 $ à 53 450 $</p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Famille formée de conjoints sans enfant</p>
                    </td>
                    <td>
                        <p>40 783 $ à 56 962 $</p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>Famille formée de conjoints avec un enfant</p>
                    </td>
                    <td>
                        <p>45 631 $ à 63 731 $</p>
                    </td>
                </tr>
                <tr>
                    <td>
                        <p>
                            Famille formée de conjoints avec deux enfants ou
                            plus
                        </p>
                    </td>
                    <td>
                        <p>48 054 $ à 67 117 $</p>
                    </td>
                </tr>
            </table>
            <p> </p>
            <p>
                Alors, si une personne n’a pas les moyens de s’offrir un avocat
                après une arrestation pour conduite avec les facultés
                affaiblies, il se peut qu’elle soit admissible à l’aide
                juridique, soit gratuitement, soit avec une contribution. Cela
                lui permet de bénéficier d’une défense appropriée avec un
                avocat, sans avoir à se ruiner!
            </p>
            <h2>
                Vous êtes accusé pour conduite avec les facultés affaiblies?
                Consultez un avocat spécialisé en droit criminel!
            </h2>
            <p>
                Si vous êtes accusé de conduite avec les facultés affaiblies,
                n’hésitez pas à consulter un avocat spécialisé en droit
                criminel. Comme vous avez pu le constater, le simple fait d’être
                accusé de cette infraction criminelle entraîne déjà des coûts
                importants, qui ne sont absolument pas comparables à ceux
                engendrés par une condamnation!
            </p>
            <p>
                Alors, pour maximiser vos chances d’obtenir un acquittement et
                éviter toutes les conséquences et{' '}
                <Link to="https://avocatsalcoolauvolant.ca/cout-alcool-volant-quebec/">
                    <strong>les coûts découlant d’une condamnation</strong>
                </Link>
                , l’assistance d’un avocat spécialisé dans le domaine est
                essentielle. Il pourra analyser votre dossier, soulever des
                moyens de défense, le cas échéant, et vous représenter
                efficacement devant le tribunal.
            </p>
            <p>
                <Img
                    fluid={data.img4.childImageSharp.fluid}
                    title="Avocat pour conduite avec les facultés affaiblies !"
                    alt=""
                />
            </p>
            <p>
                <strong>
                    Trouvez rapidement un avocat en droit criminel pour vous
                    défendre contre une accusation de conduite avec les facultés
                    affaiblies grâce à notre formulaire simple et gratuit! Une
                    fois votre demande envoyée, nous vous mettons en contact
                    avec un avocat près de chez vous.
                </strong>
            </p>
            <p>
                <strong>
                    Ce service est entièrement gratuit et sans engagement, vous
                    permettant de choisir en toute liberté et en toute
                    tranquillité!
                </strong>
            </p>
        </SeoPage>
    )
}

export default ContentPage

export const pageQuery = graphql`
    query ($language: String!) {
        locales: allLocale(filter: { language: { eq: $language } }) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }
        img0: file(
            relativePath: {
                eq: "cout-accusation-conduite-facultes-affaiblies/couts-arrestation-conduite-facultes-affaiblies.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img1: file(
            relativePath: {
                eq: "cout-accusation-conduite-facultes-affaiblies/contester-suspension-permis-conduire-quebec.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img2: file(
            relativePath: {
                eq: "cout-accusation-conduite-facultes-affaiblies/saisie-vehicule-conduite-facultes-affaiblies.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img3: file(
            relativePath: {
                eq: "cout-accusation-conduite-facultes-affaiblies/couts-avocat-conduite-facultes-affaiblies.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
        img4: file(
            relativePath: {
                eq: "cout-accusation-conduite-facultes-affaiblies/avocat-conduite-facultes-affaiblies-quebec.jpeg"
            }
        ) {
            childImageSharp {
                fluid(maxWidth: 960) {
                    ...GatsbyImageSharpFluid
                }
            }
        }
    }
`
